import React, {useContext, useEffect, useState} from "react";
import { TouchableOpacity, View} from "react-native";
import PtText from "../core/pt-text/pt-text";
import {getTranslation} from "../../helpers/getTranslation";
import PtIcon from "../core/pt-icon/pt-icon";
import styles from './pt-customer-info-styles';
import {useDispatch, useSelector} from "react-redux";
import {getCustomerDataFromApi} from "../../actions/customerDataActions";
import PtActivityIndicator from "../core/pt-activity-indicator/pt-activity-indicator";
import PtCallout from "../core/pt-callout/pt-callout";
import UserContext from "../../helpers/UserContext";
import {styleVars} from "../../app-style-vars";

/** pulled assignments out of function body to reduce cognitive complexity */
const getLabels = (props:any) => {
  return {
    customerInfoTitle : props.title || getTranslation('customer.info.title') || 'Your info',
    customerNameLabel : props.customerNameLabel || getTranslation('customer.info.name.label') || 'Name:',
    customerNumberLabel : props.customerNumberLabel || getTranslation('customer.info.number.label') || 'Customer Number:',
    customerEmailLabel : props.customerEmailLabel || getTranslation('customer.info.email.label') || 'Email:',
    customerAddressLabel : props.customerAddressLabel || getTranslation('customer.info.address.label') || 'Address:',
    customerCountryLabel : props.customerCountryLabel || getTranslation('customer.info.country.label') || 'Country',
    customerRolesLabel : props.customerRolesLabel || getTranslation('customer.info.roles.label') || 'Roles',
    noAuthText : props.noAuthText || getTranslation('customer.info.no.auth.text') || 'You are not logged in. Data shown may be incorrect.',
  }
};

const getSettings = (props:any) => {
  return {
    textColor : props.contentType && props.contentType === 'light' ? styleVars.boschWhite : styleVars.boschBlack,
    justifyItems : props.justifyItems ? props.justifyItems : 'flex-start',
    alignValues : props.alignValues ? props.alignValue : 'right',
    showTitle : props.showTitle,
    useToggle : props.useToggle,
    hideName : props.hideName,
    hideNumber : props.hideNumber,
    hideEmail : props.hideEmail,
    hideAddress : props.hideAddress,
    hideCountry : props.hideCountry,
    showRoles: props.showRoles,
  }
};

const getTitleContainerRender = (labels:any, settings:any, toggleInfoContainer:any, infoContainerVisible:any) => {
  return (
    settings.showTitle ?
      <View style={styles.titleContainer}>
        {settings.useToggle ?
          <TouchableOpacity onPress={() => toggleInfoContainer()}>
            <View style={styles.toggleHeaderContainer}>
              <PtText style={[styles.headerText, {color: settings.textColor}]}>{labels.customerInfoTitle}</PtText>
              {infoContainerVisible ?
                <PtIcon iconName={'down'} iconColor={settings.textColor}/>
                :
                <PtIcon iconName={'forward-right'} iconColor={settings.textColor}/>
              }
            </View>
          </TouchableOpacity>
          : <PtText style={[styles.headerText, {color: settings.textColor}]}>{labels.customerInfoTitle}</PtText>
        }
      </View>
      : null
  );
};

const getCustomerNameRender = (labels:any, settings:any, customerData:any) => {
  const fullName = `${customerData.firstName ? customerData.firstName : ''} ${customerData.lastName ? customerData.lastName: ''}`;
  return (
    <>
      {!settings.hideName ?
        <View style={[styles.dataItemContainer, {justifyContent: settings.justifyItems}]}>
          <PtText style={[styles.dataItemLabel,{color: settings.textColor}]}>
            {labels.customerNameLabel}
          </PtText>
          <PtText style={[styles.dataItemValue,{color: settings.textColor, textAlign: settings.alignValues}]}>
            {fullName.trim() !== '' ? fullName : '-'}
          </PtText>
        </View>
        : null
      }
    </>
  )
};

const getCustomerNumberRender = (labels:any, settings:any, customerData:any) => {
  return (
    <>
      {!settings.hideNumber ?
        <View style={[styles.dataItemContainer, {justifyContent: settings.justifyItems}]}>
          <PtText style={[styles.dataItemLabel,{color: settings.textColor}]}>
            {labels.customerNumberLabel}
          </PtText>
          <PtText style={[styles.dataItemValue,{color: settings.textColor, textAlign: settings.alignValues}]}>
            {customerData.customerNumber ? customerData.customerNumber : '-'}
          </PtText>
        </View>
        : null
      }
    </>
  )
};

const getCustomerEmailRender = (labels:any, settings:any, customerData:any) => {
  return (
    <>
      {!settings.hideEmail ?
        <View style={[styles.dataItemContainer, {justifyContent: settings.justifyItems}]}>
          <PtText style={[styles.dataItemLabel,{color: settings.textColor}]}>
            {labels.customerEmailLabel}
          </PtText>
          <PtText style={[styles.dataItemValue,{color: settings.textColor, textAlign: settings.alignValues}]}>
            {customerData.email ? customerData.email : '-'}
          </PtText>
        </View>
        : null
      }
    </>
  )
};

const getCustomerAddressRender = (labels:any, settings:any, customerData:any) => {
  return (
    <>
      {!settings.hideAddress ?
        <View style={[styles.dataItemContainer, {justifyContent: settings.justifyItems}]}>
          <PtText style={[styles.dataItemLabel,{color: settings.textColor}]}>
            {labels.customerAddressLabel}
          </PtText>
          <View style={[styles.addressValue]}>
            <PtText style={[styles.dataItemValue, {color: settings.textColor, textAlign: settings.alignValues}]} numberOfLines={3}>
              {customerData.defaultAddress ? customerData.defaultAddress : '-'}
            </PtText>
          </View>
        </View>
        : null
      }
    </>
  )
};

const getCustomerCountryRender = (labels:any, settings:any, customerData:any) => {
  return (
    <>
      {!settings.hideAddress ?
        <View style={[styles.dataItemContainer, {justifyContent: settings.justifyItems}]}>
          <PtText style={[styles.dataItemLabel,{color: settings.textColor}]}>
            {labels.customerCountryLabel}
          </PtText>
          <PtText style={[styles.dataItemValue,{color: settings.textColor, textAlign: settings.alignValues}]}>
            {customerData.country?.name ? customerData.country.name : '-'}
          </PtText>
        </View>
        : null
      }
    </>
  )
};

export default function PtCustomerInfo(props:any) {
  const labels = getLabels(props);
  const settings = getSettings(props);

  const isAuthenticated: any = useSelector((state: any) => state.authReducer.isAuthenticated);
  const loading: any = useSelector((state:any) => state.customerDataReducer.loading);
  const customerData: any = useSelector((state:any) => state.customerDataReducer);

  const [infoContainerVisible, setContainerVisible] = useState(props.showDetails ? props.showDetails : false);

  const dispatch = useDispatch();

  const {userData, updateUserData} = useContext(UserContext)

  useEffect(() => {
    if (isAuthenticated && !customerData.dataCollected) {
      console.log('----- collecting customer data from pt-customer-info component');
      getCustomerDataFromApi(dispatch);
    }
  }, [isAuthenticated]);


  useEffect(() => {
    if(isAuthenticated && customerData.dataCollected && !userData.dataCollected) {
      console.log('----- updating user data from pt-customer-info component');
      updateUserData({...userData, country: customerData.country.isocode, dataCollected: customerData.dataCollected})
    }
  }, [isAuthenticated, customerData.dataCollected, userData.dataCollected])

  const toggleInfoContainer = () => {
    setContainerVisible(!infoContainerVisible);
  };

  const renderCustomerName = (labels:any, settings:any, customerData:any) => {
    return getCustomerNameRender(labels, settings, customerData);
  };

  const renderCustomerNumber = (labels:any, settings:any, customerData:any) => {
    return getCustomerNumberRender(labels, settings, customerData);
  };

  const renderCustomerEmail = (labels:any, settings:any, customerData:any) => {
    return getCustomerEmailRender(labels, settings, customerData);
  };

  const renderCustomerAddress = (labels:any, settings:any, customerData:any) => {
    return getCustomerAddressRender(labels, settings, customerData);
  };

  const renderCustomerCountry = (labels:any, settings:any, customerData:any) => {
    return getCustomerCountryRender(labels, settings, customerData);
  };

  const renderTitleContainer = (labels:any, settings:any, toggleInfoContainer:any, infoContainerVisible:any) => {
    return getTitleContainerRender(labels, settings, toggleInfoContainer, infoContainerVisible);
  };

  const getErrorDescription = () => {
    console.log('errror je ', typeof customerData.errors);
    if( typeof customerData.errors == 'object' ) {
      return customerData.errors.message ? customerData.errors.message : 'Error encountered while fetching customer data';
    } else {
      return customerData.errors;
    }
  }

  return (
      !customerData.errors ?
        <View style={{}}>
          <PtActivityIndicator show={loading}/>

          {renderTitleContainer(labels, settings, toggleInfoContainer, infoContainerVisible)}

          {settings.useToggle && infoContainerVisible || !settings.useToggle ?
            <>
              <View style={{paddingTop: 4}}>
                {renderCustomerName(labels, settings, customerData)}
                {renderCustomerNumber(labels, settings, customerData)}
                {renderCustomerEmail(labels, settings, customerData)}
                {renderCustomerAddress(labels, settings, customerData)}
                {renderCustomerCountry(labels, settings, customerData)}
              </View>

              {!isAuthenticated ?
                  <PtText style={[styles.noAuthText, {color: settings.textColor}]}>
                    {labels.noAuthText}
                  </PtText>
                : null
              }
              </>
            : null
          }
        </View>
        :
        <View style={{backgroundColor: '#fff'}}>
          <PtCallout type={'error'} message={getErrorDescription()}/>
        </View>
  )
}
